import React, { useState } from 'react';
import AttachmentUpload from './AttachmentUpload';
import CommonOptionFields from './CommonOptionFields';

const GeneralItemForm = ({ onSave, onCancel, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    itemName: initialData?.itemName || '',
    category: initialData?.category || '',
    quantity: initialData?.quantity || '1',
    unitPrice: initialData?.unitPrice || '',
    totalPrice: initialData?.totalPrice || initialData?.price || '',
    supplier: initialData?.supplier || '',
    confirmationNumber: initialData?.confirmationNumber || '',
    date: initialData?.date || '',
    description: initialData?.description || '',
    depositRequired: initialData?.depositRequired || false,
    depositAmount: initialData?.depositAmount || '',
    depositDueDate: initialData?.depositDueDate || '',
    isOption: initialData?.isOption || false,
    optionDescription: initialData?.optionDescription || '',
    paymentTiming: initialData?.paymentTiming || 'with_deposit',
    optionalDepositAmount: initialData?.optionalDepositAmount || '',
    agentNotes: initialData?.agentNotes || '',
    agentCommission: initialData?.agentCommission ?? null,
    photos: initialData?.photos || [],
    documents: initialData?.documents || []
  });

  const itemCategories = [
    'Travel Accessories',
    'Travel Insurance Add-ons',
    'Visa/Passport Services',
    'Airport Services',
    'WiFi/Communication',
    'Transfers',
    'Tickets/Passes',
    'Equipment Rental',
    'Guides/Maps',
    'Other'
  ];

  // Calculate total price when quantity or unit price changes
  React.useEffect(() => {
    const quantity = parseInt(formData.quantity) || 1;
    const unitPrice = parseFloat(formData.unitPrice) || 0;
    const total = quantity * unitPrice;
    setFormData(prev => ({
      ...prev,
      totalPrice: total > 0 ? total.toFixed(2) : ''
    }));
  }, [formData.quantity, formData.unitPrice]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      price: parseFloat(formData.totalPrice) || parseFloat(formData.unitPrice) || 0,
      quantity: parseInt(formData.quantity) || 1,
      depositAmount: formData.depositRequired ? parseFloat(formData.depositAmount) || 0 : 0,
      paymentTiming: formData.paymentTiming,
      optionalDepositAmount: formData.optionalDepositAmount ? parseFloat(formData.optionalDepositAmount) : null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="component-form general-item-form">
      <div className="form-section">
        <h4>Item Information</h4>
        
        <div className="form-group">
          <label>Title/Description*</label>
          <input
            type="text"
            value={formData.title}
            onChange={(e) => setFormData({...formData, title: e.target.value})}
            placeholder="e.g., London City Pass - 3 Day"
            required
          />
        </div>

        <div className="form-row">
          <div className="form-group">
            <label>Item Name*</label>
            <input
              type="text"
              value={formData.itemName}
              onChange={(e) => setFormData({...formData, itemName: e.target.value})}
              placeholder="e.g., City Pass, Travel Insurance, SIM Card"
              required
            />
          </div>
          <div className="form-group">
            <label>Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({...formData, category: e.target.value})}
            >
              <option value="">Select Category</option>
              {itemCategories.map(cat => (
                <option key={cat} value={cat}>{cat}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Pricing & Quantity</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Quantity</label>
            <input
              type="number"
              value={formData.quantity}
              onChange={(e) => setFormData({...formData, quantity: e.target.value})}
              placeholder="1"
              min="1"
            />
          </div>
          <div className="form-group">
            <label>Unit Price*</label>
            <input
              type="number"
              value={formData.unitPrice}
              onChange={(e) => setFormData({...formData, unitPrice: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              required
            />
          </div>
          <div className="form-group">
            <label>Total Price</label>
            <input
              type="number"
              value={formData.totalPrice}
              onChange={(e) => setFormData({...formData, totalPrice: e.target.value})}
              placeholder="0.00"
              min="0"
              step="0.01"
              readOnly={formData.quantity && formData.unitPrice}
            />
          </div>
        </div>
      </div>

      <div className="form-section">
        <h4>Additional Details</h4>
        
        <div className="form-row">
          <div className="form-group">
            <label>Supplier/Provider</label>
            <input
              type="text"
              value={formData.supplier}
              onChange={(e) => setFormData({...formData, supplier: e.target.value})}
              placeholder="e.g., London Tourism Board"
            />
          </div>
          <div className="form-group">
            <label>Confirmation Number</label>
            <input
              type="text"
              value={formData.confirmationNumber}
              onChange={(e) => setFormData({...formData, confirmationNumber: e.target.value})}
              placeholder="e.g., ABC123456"
            />
          </div>
          <div className="form-group">
            <label>Valid Date/Use Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({...formData, date: e.target.value})}
            />
          </div>
        </div>

        <div className="form-group">
          <label>Item Description & Details</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({...formData, description: e.target.value})}
            placeholder="Include any important details, restrictions, inclusions, how to use, etc..."
            rows="4"
          />
        </div>
      </div>

      <CommonOptionFields formData={formData} setFormData={setFormData} />

      <AttachmentUpload
        photos={formData.photos}
        documents={formData.documents}
        onPhotosChange={(photos) => setFormData({...formData, photos})}
        onDocumentsChange={(documents) => setFormData({...formData, documents})}
        maxPhotos={5}
        maxDocuments={3}
        showPhotos={true}
        showDocuments={true}
      />

      <div className="form-actions">
        <button type="button" className="btn btn-secondary" onClick={onCancel}>
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {initialData ? 'Update' : 'Add'} Item
        </button>
      </div>
    </form>
  );
};

export default GeneralItemForm;
